import { CheckCircle2Icon, Loader2Icon, XCircleIcon } from 'lucide-react';
import { APP_VERSION } from '@gb-ticket/shared-types';

import { Button } from '@/components/ui/button';
import { useHealth } from '@/products/helpdesk/features/health/use-health';
import { ApiRequestError } from '@/lib/api';

const componentLabels: Record<string, string> = {
  database: 'Datenbank (PostgreSQL)',
  redis: 'Redis',
};

export function StatusPage() {
  const health = useHealth();
  const details = Object.entries(health.data?.details ?? {}).filter(([key]) => key !== 'version');
  const reportedVersion =
    (health.data?.details?.version as { version?: string } | undefined)?.version ?? APP_VERSION;

  return (
    <section className="mx-auto max-w-xl space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Systemstatus</h1>
          <p className="mt-1 text-sm text-muted-foreground">Version {reportedVersion}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void health.refetch()}
          disabled={health.isFetching}
        >
          {health.isFetching && <Loader2Icon className="animate-spin" aria-hidden />}
          Aktualisieren
        </Button>
      </div>

      {health.isPending && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden /> Status wird geladen …
        </p>
      )}

      {health.isError && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/10 p-4"
        >
          <XCircleIcon className="mt-0.5 size-5 shrink-0 text-destructive" aria-hidden />
          <div>
            <p className="font-medium">Backend nicht erreichbar</p>
            <p className="text-sm text-muted-foreground">
              {health.error instanceof ApiRequestError
                ? health.error.message
                : 'Bitte prüfen, ob Backend, Datenbank und Redis laufen (pnpm db:up, pnpm dev).'}
            </p>
          </div>
        </div>
      )}

      {health.data && (
        <ul className="divide-y rounded-md border">
          {details.map(([key, value]) => {
            const status = (value as { status?: string }).status;
            const up = status === 'up';
            return (
              <li key={key} className="flex items-center justify-between px-4 py-3">
                <span>{componentLabels[key] ?? key}</span>
                <span className="flex items-center gap-2 text-sm">
                  {up ? (
                    <CheckCircle2Icon className="size-4 text-green-600" aria-hidden />
                  ) : (
                    <XCircleIcon className="size-4 text-destructive" aria-hidden />
                  )}
                  {up ? 'Betriebsbereit' : 'Gestört'}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
